import { ActionIcon, Menu } from '@mantine/core';
import {
  IconCheck,
  IconDotsVertical,
  IconMessageShare,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { Claim } from 'interfaces';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import {
  Action,
  ActionHandlerContext,
  ActionMenuEntry,
  ActionMenuGroup,
  ActionMetadata,
  partitionActionsForMenu,
} from 'utils/actionsUtils';
import { claimHasTariffStatus } from 'utils/claimStatusUtils';
import { useRemoteFeatureFlag } from 'hooks/useRemoteFeatureFlag';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useChatUIStore } from 'store/ChatStore';

interface ClaimActionsMenuProps {
  claim: Claim;
  actions: ActionMetadata[];
  actionContext: ActionHandlerContext;
  isProvider: boolean;
  canModify: boolean;
}

const isActionDisabled = (
  action: ActionMetadata,
  actionContext: ActionHandlerContext
): boolean => {
  return action.disabled ? action.disabled(actionContext) : false;
};

export const ClaimActionsMenu = ({
  claim,
  actions,
  actionContext,
  isProvider,
  canModify,
}: ClaimActionsMenuProps) => {
  const { translate } = useTranslation();
  const chatEnabled = useRemoteFeatureFlag('chat');
  const isAdmin = useIsAdmin();
  const stageClaim = useChatUIStore((state) => state.stageClaim);
  const menuEntries = partitionActionsForMenu(actions);

  const renderActionMenuItem = (action: ActionMetadata) =>
    (() => {
      const disabled = isActionDisabled(action, actionContext);
      const supportingDocumentDisabledReason = (() => {
        if (!disabled) return undefined;
        if (action.type === Action.VIEW_SOURCE_DATA) {
          return translate(
            'pages:claims.sourceData.disabledReason',
            'No source message is available for this claim.'
          );
        }
        if (action.type === Action.VIEW_REQUESTS) {
          return translate(
            'pages:claims.actions.menu.noActionRequests',
            'No action requests have been created for this claim yet.'
          );
        }
        if (action.type === Action.UPLOAD_SUPPORTING_DOCUMENT) {
          return translate(
            'pages:claims.supportingDocument.disabledReason',
            'This Manitoba claim is missing processed claim details or you do not have permission to submit queries.'
          );
        }
        return undefined;
      })();

      return (
        <Menu.Item
          key={action.type}
          onClick={() => {
            action.execute(actionContext);
          }}
          disabled={disabled}
          leftSection={action.icon}
          title={supportingDocumentDisabledReason}
        >
          {action.label}
        </Menu.Item>
      );
    })();

  const renderGroupMenuItem = (
    entry: Extract<ActionMenuEntry, { kind: 'group' }>
  ) => {
    const isGroupDisabled =
      entry.actions.length > 0 &&
      entry.actions.every((action) => isActionDisabled(action, actionContext));
    const leftSection =
      entry.group === ActionMenuGroup.MARK_AS_TRANSITIONS ? (
        <IconCheck size={14} />
      ) : undefined;
    const groupLabel =
      entry.group === ActionMenuGroup.MARK_AS_TRANSITIONS
        ? translate('pages:claims.actions.markAsTransitions', 'Mark As')
        : entry.group;

    return (
      <Menu.Sub key={`group-${entry.group}`}>
        <Menu.Sub.Target>
          <Menu.Sub.Item leftSection={leftSection} disabled={isGroupDisabled}>
            {groupLabel}
          </Menu.Sub.Item>
        </Menu.Sub.Target>
        <Menu.Sub.Dropdown>
          {entry.actions.map(renderActionMenuItem)}
        </Menu.Sub.Dropdown>
      </Menu.Sub>
    );
  };

  return (
    <Menu shadow="md" width={180} position="right-start" withinPortal>
      <Menu.Target>
        <ActionIcon
          size={24}
          variant="subtle"
          aria-label={translate(
            'pages:claims.actions.menu.ariaLabel',
            'Claim actions'
          )}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            actionContext.setSelectedClaim?.(claim);
          }}
        >
          <IconDotsVertical size={18} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          {translate('pages:claims.actions.menu.actionsLabel', 'Actions')}
        </Menu.Label>
        {menuEntries.map((entry) =>
          entry.kind === 'action'
            ? renderActionMenuItem(entry.action)
            : renderGroupMenuItem(entry)
        )}
        {chatEnabled && !isAdmin && (
          <Menu.Item
            leftSection={<IconMessageShare size={14} />}
            onClick={() => stageClaim(claim.id)}
          >
            {translate('pages:claims.actions.shareToChat')}
          </Menu.Item>
        )}
        <Menu.Divider />
        {!isProvider && (
          <>
            <Menu.Label>
              {translate('pages:claims.actions.menu.dangerZone', 'Danger Zone')}
            </Menu.Label>
            <Menu.Item
              color="red"
              onClick={() => {
                actionContext.setSelectedClaim?.(claim);
                actionContext.setCancelClaimOpen?.(true);
              }}
              leftSection={<IconX size={14} />}
              disabled={
                claimHasTariffStatus(claim, CLAIM_TARIFF_STATUS.SUBMITTED) ||
                claimHasTariffStatus(claim, CLAIM_TARIFF_STATUS.PENDING) ||
                !canModify
              }
            >
              {translate(
                'pages:claims.actions.menu.cancelClaim',
                'Cancel Claim'
              )}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
